import { Component, inject, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { baseRequest } from 'src/app/common/classes/request-builder';
import { StorageManager } from 'src/app/common/storage-manager.class';
import { Utils } from 'src/app/common/utils';

import { BaseForm } from 'src/app/view/base-form';

import { DashboardService } from 'src/app/service/events/dashboard.service';
import { FormCommonApiService } from 'src/app/service/api/formcommon-api.service';
import { ProductosApiService } from 'src/app/service/api/productos-api';
import { ClientesApiService } from 'src/app/service/api/clientes-api.service';
import { TratamientosApiService } from 'src/app/service/api/tratamientos-api.service';

import { ClienteModel } from 'src/app/models/cliente.model';
import { ProductosModel } from 'src/app/models/productos/productos.model';
import { Uso } from 'src/app/models/productos/uso.model';
import { UserModel } from 'src/app/models/usuarios/user.model';
import { TratamientosModel } from 'src/app/models/tratamientos/tratamientos.model';

import { TratamientosFormDefinition } from './form-control/form-definition';
import { DropdownData } from 'src/app/common/components/reactive-form-builder/interfaces/input.interface';
import {
    ReactiveFormChangedField,
    ReactiveFormValue
} from 'src/app/common/components/reactive-form-builder/interfaces/reactive-form-change.interface';
import { BrowserDiagnosticsService } from 'src/app/common/diagnostics/browser-diagnostics.service';
import { ProductCatalogCacheService } from 'src/app/service/cache/product-catalog-cache.service';
import { FormType } from 'src/app/common/components/reactive-form-builder/interfaces/form.interface';

type CachedProductDetail = {
    usos: Array<{ label: string; value: Uso | null }>;
    agentes: Array<{ label: string; value: Uso | null }>;
};

type ProductDropdownOption = { label: string; value: ProductosModel };
type AgenteDropdownOption = { label: string; value: Uso };
type UnitDropdownOption = { label: string; value: string | null };

type UnidadMedidaApiRow = {
    unidades_de_medida?: string | number | null;
    unidad_medida?: string | number | null;
    unidad?: string | number | null;
    label?: string | number | null;
    value?: string | number | null;
};

const COMMON_UNIT_OPTIONS = ['kg/ha', 'L/ha', 'kg', 'L', '%', 'cc/100 L', 'cc/100L'] as const;

const PRODUCT_CACHE_TTL_MS = 300000;
const PRODUCT_DETAIL_CACHE_TTL_MS = 900000;

@Component({
    selector: 'app-tratamientos-form',
    standalone: false,
    templateUrl: './tratamientos-form.component.html',
    styleUrls: ['./tratamientos-form.component.scss']
})
export class TratamientosFormComponent extends BaseForm<TratamientosModel> implements OnInit {
    public form!: TratamientosFormDefinition;

    public loadingData: boolean = true;

    /**
     * Stable snapshot used only to bootstrap the builder.
     * It must NOT be replaced with live form changes, otherwise the child builder
     * receives a new previousData input on every edit and re-patches itself.
     */
    public initialPreviousData: TratamientosModel | null = null;

    /**
     * Last runtime snapshot received from the builder.
     * Used for read operations only; not fed back into [previousData].
     */
    private runtimeModelSnapshot: TratamientosModel | null = null;

    /**
     * Cache MAGRAMA detail by registration number to avoid repeated requests.
     */
    private readonly productDetailCache = new Map<string, CachedProductDetail>();

    private readonly productDetailRequests = new Map<string, Promise<void>>();

    private rawClients: ClienteModel[] = [];

    private rawProducts: ProductosModel[] = [];

    private readonly productById = new Map<string, ProductosModel>();

    private baseSelectableProductOptions: ProductDropdownOption[] = [];

    private allProductOptions: ProductDropdownOption[] = [];

    private allAgenteOptions: AgenteDropdownOption[] = [];

    private lastProductOptionsSignature = '';

    private readonly diagnostics = inject(BrowserDiagnosticsService, { optional: true });

    private readonly productCatalogCache = inject(ProductCatalogCacheService);

    public constructor(
        public tratamientosApi: TratamientosApiService,
        public formApi: FormCommonApiService,
        public productosApi: ProductosApiService,
        public clientesApi: ClientesApiService,
        public override route: ActivatedRoute,
        public override router: Router,
        public dashboardEvents: DashboardService
    ) {
        super(
            route,
            router,
            dashboardEvents,
            tratamientosApi.tratamientosPUT,
            tratamientosApi.tratamientosPOST,
            'tratamientos',
            'Editar Tratamiento',
            'Crear Tratamiento',
            'Duplicar Tratamiento'
        );
    }

    public ngOnInit(): void {
        const scope = this.diagnostics?.start('ng-on-init', 'tratamientos-form', this.getDiagnosticsMetadata());

        this.dropdownsFinalized = false;

        this.form = new TratamientosFormDefinition(
            this.getType(),
            this.getModel() as TratamientosModel,
            null
        );

        this.form.model = {
            ...this.form.model,
            ...(this.getModel() as TratamientosModel)
        };

        this.initialPreviousData = { ...this.form.model };
        this.runtimeModelSnapshot = { ...this.form.model };

        this.form.usosResolver = (producto: ProductosModel | null) => {
            if (!producto || !producto.num_registro || producto.tipo === 'fertilizante') {
                return {
                    values: [{ label: 'Uso...', value: null }],
                    filtered: [{ label: 'Uso...', value: null }],
                    selected: null
                };
            }

            const detail = this.getCachedProductDetail(String(producto.num_registro));

            if (!detail) {
                return {
                    values: [{ label: 'Cargando...', value: null }],
                    filtered: [{ label: 'Cargando...', value: null }],
                    selected: null
                };
            }

            return {
                values: detail.usos,
                filtered: detail.usos,
                selected: null
            };
        };

        this.form.agentesResolver = (producto: ProductosModel | null, uso: Uso | null) => {
            if (!producto) {
                return this.emptyAgentesDropdown();
            }

            if (this.isFertilizerProduct(producto)) {
                return this.getFertilizerAgentesDropdown();
            }

            if (!producto.num_registro) {
                return this.emptyAgentesDropdown();
            }

            const detail = this.getCachedProductDetail(String(producto.num_registro));

            if (!detail) {
                return {
                    values: [{ label: 'Cargando...', value: null }],
                    filtered: [{ label: 'Cargando...', value: null }],
                    selected: null
                };
            }

            const filteredAgentes = uso
                ? detail.agentes.filter((item) => {
                    return String(item.value?.uso ?? '').trim().toLowerCase()
                        === String(uso.uso ?? '').trim().toLowerCase();
                })
                : detail.agentes;

            const resolvedAgentes = filteredAgentes.length > 0 || !uso
                ? filteredAgentes
                : detail.agentes;

            const deduplicatedAgentes = this.deduplicateOptionsByLabel(resolvedAgentes);
            const values = [{ label: 'Agente...', value: null }, ...deduplicatedAgentes];

            return {
                values,
                filtered: values,
                selected: null
            };
        };

        void this.initFormData();
        this.softInit(this.getType());
        scope?.end(this.getDiagnosticsMetadata());
    }

    public formChanges(
        formModel: ReactiveFormValue<TratamientosModel>, 
        lastChangedFieldName: ReactiveFormChangedField<TratamientosModel>
    ): void {
        const scope = this.diagnostics?.start('form-changes', 'tratamientos-form', {
            ...this.getDiagnosticsMetadata(),
            field: String(lastChangedFieldName),
        });

        this.runtimeModelSnapshot = { ...(formModel as TratamientosModel) };
        this.form.tag = lastChangedFieldName;

        if (lastChangedFieldName === 'id_cliente') {
            this.postProcessClientes();
            this.dropdownLoadTrigger.next();
            scope?.end(this.getDiagnosticsMetadata());
            return;
        }

        if (lastChangedFieldName === '__repeatable__' || lastChangedFieldName === 'ids_productos') {
            this.postProcessProductos();

            void this.ensureDependentDataLoaded().finally(() => {
                this.dropdownLoadTrigger.next();
            });

            scope?.end(this.getDiagnosticsMetadata());
            return;
        }

        this.dropdownLoadTrigger.next();
        scope?.end(this.getDiagnosticsMetadata());
    }

    public getCachedProductDetail(registro: string): CachedProductDetail | null {
        return this.productDetailCache.get(registro) ?? null;
    }

    private normalizePrimaryKey(value: unknown, primaryKey: string = 'id'): string {
        if (value == null) {
            return '';
        }

        if (typeof value === 'object') {
            return String((value as Record<string, unknown>)[primaryKey] ?? '').trim();
        }

        return String(value).trim();
    }

    private getRuntimeModelSnapshot(): TratamientosModel {
        const builderValue = this.formBuilderRef?.model?.getRawValue?.() as TratamientosModel | undefined;

        if (builderValue) {
            return builderValue;
        }

        if (this.runtimeModelSnapshot) {
            return this.runtimeModelSnapshot;
        }

        return this.form.model;
    }

    private normalizeResponseArray<T extends object>(response: unknown): T[] {
        if (Array.isArray(response)) {
            return response.filter((item): item is T => item instanceof Object);
        }

        if (typeof response !== 'string') {
            return [];
        }

        return this.decodeResponseArray<T>(response).filter((item): item is T => item instanceof Object);
    }

    /**
     * Product endpoints are base64-decoded by DecodeInterceptor. This fallback keeps the
     * form working if an endpoint is missing from that interceptor or a stale in-memory
     * cache contains the encoded payload.
     */
    private decodeResponseArray<T>(encodedResponse: string): T[] {
        const trimmedResponse = encodedResponse.trim();

        if (trimmedResponse === '') {
            return [];
        }

        const directParsed = this.parseJsonArray<T>(trimmedResponse);

        if (directParsed) {
            return directParsed;
        }

        try {
            const decodedResponse = window.atob(trimmedResponse);

            return this.parseJsonArray<T>(decodedResponse) ?? [];
        } catch {
            return [];
        }
    }

    private parseJsonArray<T>(rawValue: string): T[] | null {
        try {
            const parsedValue = JSON.parse(rawValue) as unknown;

            return Array.isArray(parsedValue) ? parsedValue as T[] : null;
        } catch {
            return null;
        }
    }

    private mapClientsToOptions(clients: ClienteModel[]): Array<{ label: string; value: ClienteModel }> {
        return clients.map((item) => ({
            label: item.nombre || item.razon_social || '',
            value: item
        }));
    }

    private mapProductsToOptions(products: ProductosModel[]): ProductDropdownOption[] {
        return products.map((item) => ({
            label: this.buildProductDropdownLabel(item),
            value: item
        }));
    }

    private mapAgentesToOptions(agentes: Uso[]): AgenteDropdownOption[] {
        return this.deduplicateOptionsByLabel(
            agentes
                .map((item) => ({
                    label: String(item.agente ?? '').trim(),
                    value: item
                }))
                .filter((item) => item.label !== '')
        );
    }

    private emptyAgentesDropdown(): DropdownData<Uso> {
        return {
            values: [{ label: 'Agente...', value: null }],
            filtered: [{ label: 'Agente...', value: null }],
            selected: null
        };
    }

    private getFertilizerAgentesDropdown(): DropdownData<Uso> {
        if (!this.shouldLoadDistinctAgentes() || this.allAgenteOptions.length === 0) {
            return this.emptyAgentesDropdown();
        }

        const values = [{ label: 'Agente...', value: null }, ...this.allAgenteOptions];

        return {
            values,
            filtered: values,
            selected: null
        };
    }

    private isFertilizerProduct(product: ProductosModel): boolean {
        return String(product.tipo ?? '').trim().toLowerCase() === 'fertilizante';
    }

    private shouldLoadDistinctAgentes(): boolean {
        return this.form.env.appName === 'hernandorena' && this.form.env.showTratamientosAllProductos;
    }

    private buildProductDropdownLabel(product: ProductosModel): string {
        const prefix = this.getProductDropdownPrefix(product);
        const productName = String(product.nombre ?? '').trim();

        return `${prefix} ${productName}`.trim();
    }

    private getProductDropdownPrefix(product: ProductosModel): string {
        const productType = String(product.tipo ?? '').trim().toLowerCase();

        if (productType === 'fertilizante') {
            return Utils.checkIfProductIsValid(product.fecha_caducidad, productType);
        }

        const registrationNumber = String(product.num_registro ?? '').trim();

        if (productType === 'fitosanitario' && registrationNumber === '') {
            return Utils.checkIfProductIsValid('', productType);
        }

        if (String(product.fecha_caducidad ?? '').trim() !== '') {
            return Utils.checkIfProductIsValid(product.fecha_caducidad, productType);
        }

        return '';
    }

    private rebuildProductIndexes(): void {
        this.productById.clear();

        const selectableProducts = new Map<string, ProductosModel>();
        const allProducts = new Map<string, ProductosModel>();

        for (const item of this.rawProducts) {
            const productId = String(item.id ?? '').trim();

            if (productId === '' || allProducts.has(productId)) {
                continue;
            }

            const normalizedProduct = this.normalizeProductForDropdown(item);

            this.productById.set(productId, normalizedProduct);
            allProducts.set(productId, normalizedProduct);

            if (this.form.env.showTratamientosAllProductos || this.isTreatmentProduct(normalizedProduct)) {
                selectableProducts.set(productId, normalizedProduct);
            }
        }

        const sortByName = (a: ProductosModel, b: ProductosModel): number => {
            return String(a.nombre ?? '').localeCompare(String(b.nombre ?? ''));
        };

        this.allProductOptions = this.mapProductsToOptions([...allProducts.values()].sort(sortByName));
        this.baseSelectableProductOptions = this.mapProductsToOptions([...selectableProducts.values()].sort(sortByName));
        this.form.setProductIndex([...allProducts.values()]);
    }

    private normalizeProductForDropdown(product: ProductosModel): ProductosModel {
        return {
            ...product,
            nombre: String(product.nombre ?? '').trim().toUpperCase()
        };
    }

    private isTreatmentProduct(product: ProductosModel): boolean {
        return product.tipo === 'fitosanitario' || product.tipo === 'fertilizante';
    }


    private normalizeOptionLabel(value: unknown): string {
        return String(value ?? '')
            .trim()
            .replace(/\s+/g, ' ')
            .toLowerCase();
    }

    private deduplicateOptionsByLabel<T extends { label: string }>(options: T[]): T[] {
        const seenLabels = new Set<string>();

        return options.filter((item) => {
            const normalizedLabel = this.normalizeOptionLabel(item.label);

            if (normalizedLabel === '' || seenLabels.has(normalizedLabel)) {
                return false;
            }

            seenLabels.add(normalizedLabel);
            return true;
        });
    }

    private getSelectedClientId(): string {
        const currentModel = this.getRuntimeModelSnapshot();

        return this.normalizePrimaryKey(currentModel.id_cliente, 'id');
    }

    private getSelectedProductIds(): Set<string> {
        const currentModel = this.getRuntimeModelSnapshot();

        return new Set(
            String(currentModel.ids_productos ?? '')
                .split(';')
                .map((item) => item.trim())
                .filter((item) => item !== '')
        );
    }

    private initFormData(): Promise<void> {
        const scope = this.diagnostics?.start('init-form-data', 'tratamientos-form', this.getDiagnosticsMetadata());

        this.loadingData = true;

        return Promise.all([
            baseRequest(this.clientesApi.clientesGET),
            this.productCatalogCache.getOrLoad<unknown>(
                this.getProductCacheConfig('productos-dropdown-get'),
                () => baseRequest(this.productosApi.productosDropdown.GET) as Promise<unknown>
            ),
            this.shouldLoadDistinctAgentes()
                ? baseRequest(this.formApi.mgAgentes, { getall: true } as never)
                : Promise.resolve([]),
            baseRequest(this.formApi.mgUnidadesMedida)
        ]).then(([clientsResponse, productsResponse, agentsResponse, unitsResponse]) => {
            this.rawClients = this.normalizeResponseArray<ClienteModel>(clientsResponse);
            this.rawProducts = this.normalizeResponseArray<ProductosModel>(productsResponse);
            this.allAgenteOptions = this.mapAgentesToOptions(this.normalizeResponseArray<Uso>(agentsResponse));
            this.populateUnitOptions(this.normalizeResponseArray<UnidadMedidaApiRow>(unitsResponse));

            this.rebuildProductIndexes();

            const clientOptions = this.mapClientsToOptions(this.rawClients);

            this.form.lists.clientes.filtered = [...clientOptions];
            this.form.lists.clientes.values = [...clientOptions];

            this.form.lists.productosAll.filtered = [...this.allProductOptions];
            this.form.lists.productosAll.values = [...this.allProductOptions];

            this.postProcessClientes();
            this.postProcessProductos();
            this.applyInitialState();

            this.loadingData = false;
            this.dropdownsFinalized = true;
            this.dropdownLoadTrigger.next();
            scope?.addMetric('clients', this.rawClients.length, 'items');
            scope?.addMetric('products', this.rawProducts.length, 'items');
            scope?.addMetric('units', this.form.lists.unidadesMedida.filtered.length, 'items');
            scope?.end(this.getDiagnosticsMetadata());

            setTimeout(() => {
                void this.ensureDependentDataLoaded().finally(() => {
                    this.dropdownLoadTrigger.next();
                });
            }, 0);
        }).catch((error: unknown) => {
            scope?.fail(error, this.getDiagnosticsMetadata());
            console.error('Error loading tratamientos form data:', error);
            this.loadingData = false;
            this.dropdownsFinalized = true;
        });
    }

    private populateUnitOptions(units: UnidadMedidaApiRow[]): void {
        const optionByKey = new Map<string, UnitDropdownOption>();

        for (const unit of COMMON_UNIT_OPTIONS) {
            this.addUnitOption(optionByKey, unit);
        }

        for (const unit of units) {
            this.addUnitOption(optionByKey, this.extractUnitLabel(unit));
        }

        const unitOptions: UnitDropdownOption[] = [
            { label: '...', value: null },
            ...[...optionByKey.values()].sort((firstOption, secondOption) => {
                return firstOption.label.localeCompare(secondOption.label);
            })
        ];

        this.form.lists.unidadesMedida.filtered = unitOptions;
        this.form.lists.unidadesMedida.values = [...unitOptions];
    }

    private addUnitOption(optionByKey: Map<string, UnitDropdownOption>, rawUnit: unknown): void {
        const label = String(rawUnit ?? '').trim();
        const key = this.normalizeUnitOptionKey(label);

        if (label === '' || key === '' || optionByKey.has(key)) {
            return;
        }

        optionByKey.set(key, { label, value: label });
    }

    private extractUnitLabel(unit: UnidadMedidaApiRow): string {
        return String(
            unit.unidades_de_medida
            ?? unit.unidad_medida
            ?? unit.unidad
            ?? unit.label
            ?? unit.value
            ?? ''
        ).trim();
    }

    private normalizeUnitOptionKey(value: unknown): string {
        return String(value ?? '')
            .trim()
            .replace(/\s+/g, '')
            .toLowerCase();
    }

    private postProcessClientes(): void {
        const user = StorageManager.getUser() as UserModel;

        const source = [...this.rawClients];

        let filteredClients = source;

        if (!this.form.env.showTareasMto) {
            if (!(this.form.env.appName === 'sequiador' && user.rol === 'admin')) {
                filteredClients = source.filter((item) => {
                    return [item.id_usuario_asignado, item.id_usuario_asignado2].includes(user.id);
                });
            }
        }

        const selectedClientId = this.getSelectedClientId();

        if (selectedClientId !== '') {
            const selectedClient = source.find((item) => {
                return String(item.id ?? '') === selectedClientId;
            }) ?? null;

            if (selectedClient && !filteredClients.some((item) => String(item.id ?? '') === selectedClientId)) {
                filteredClients = [selectedClient, ...filteredClients];
            }
        }

        filteredClients = [...filteredClients].sort((a, b) => {
            return String(a.nombre ?? a.razon_social ?? '').localeCompare(
                String(b.nombre ?? b.razon_social ?? '')
            );
        });

        this.form.lists.clientes.filtered = this.mapClientsToOptions(filteredClients);

        this.form.lists.clientes.values = [...this.form.lists.clientes.filtered];

        if (this.getType() === FormType.CREATION) {
            const currentClientId = String(StorageManager.getClient()?.id ?? '');

            const currentClient = filteredClients.find((item) => {
                return String(item.id ?? '') === currentClientId;
            }) ?? null;

            if (currentClient) {
                this.form.lists.clientes.selected = currentClient;
                this.form.model.id_cliente = String(currentClient.id ?? '');
                return;
            }
        }

        const selectedClient = filteredClients.find((item) => {
            return String(item.id ?? '') === selectedClientId;
        }) ?? null;

        if (selectedClient) {
            this.form.lists.clientes.selected = selectedClient;

            const clientControl = this.formBuilderRef?.model?.get('id_cliente');
            const currentControlId = this.normalizePrimaryKey(clientControl?.value, 'id');

            if (clientControl && (currentControlId === '' || currentControlId === selectedClientId)) {
                clientControl.patchValue(selectedClient, { emitEvent: false });
            }
        }
    }

    private postProcessProductos(): void {
        const selectedProductIds = this.getSelectedProductIds();
        const selectedSignature = [...selectedProductIds].sort().join('|');
        const optionsSignature = [
            this.rawProducts.length,
            this.form.env.showTratamientosAllProductos ? 'all' : 'limited',
            selectedSignature
        ].join(':');

        if (optionsSignature === this.lastProductOptionsSignature) {
            return;
        }

        const scope = this.diagnostics?.start('post-process-productos', 'tratamientos-form', this.getDiagnosticsMetadata());
        const optionByProductId = new Map<string, ProductDropdownOption>();

        for (const option of this.baseSelectableProductOptions) {
            optionByProductId.set(String(option.value.id ?? ''), option);
        }

        for (const productId of selectedProductIds) {
            if (optionByProductId.has(productId)) {
                continue;
            }

            const selectedProduct = this.productById.get(productId);

            if (selectedProduct) {
                optionByProductId.set(productId, {
                    label: this.buildProductDropdownLabel(selectedProduct),
                    value: selectedProduct
                });
            }
        }

        const productOptions = [...optionByProductId.values()]
            .sort((a, b) => String(a.value.nombre ?? '').localeCompare(String(b.value.nombre ?? '')));

        this.form.lists.productos.filtered = productOptions;
        this.form.lists.productos.values = [...productOptions];
        this.lastProductOptionsSignature = optionsSignature;

        scope?.addMetric('sourceProducts', this.rawProducts.length, 'items');
        scope?.addMetric('filteredProducts', productOptions.length, 'items');
        scope?.addMetric('selectedProducts', selectedProductIds.size, 'items');
        scope?.end(this.getDiagnosticsMetadata());
    }

    private applyInitialState(): void {
        if (this.getType() === FormType.CREATION) {
            const user = StorageManager.getUser() as UserModel;
            const clientId = user.tipo === 'comunero'
                ? String(user.id_cliente ?? '')
                : String(StorageManager.getClient()?.id ?? '');

            if (clientId !== '') {
                this.form.model.id_cliente = clientId;
            }
        }

        if (this.getType() === FormType.DUPLICATION) {
            delete this.form.model.id;
        }
    }

    private async ensureDependentDataLoaded(): Promise<void> {
        const scope = this.diagnostics?.start('ensure-dependent-data-loaded', 'tratamientos-form', this.getDiagnosticsMetadata());
        const selectedProducts = this.getSelectedProductsFromModel();

        for (const product of selectedProducts) {
            const registro = String(product.num_registro ?? '').trim();

            if (registro === '' || product.tipo === 'fertilizante') {
                continue;
            }

            if (this.productDetailCache.has(registro)) {
                continue;
            }

            await this.fetchProductDetail(product);
        }

        scope?.addMetric('selectedProducts', selectedProducts.length, 'items');
        scope?.addMetric('cachedProducts', this.productDetailCache.size, 'items');
        scope?.end(this.getDiagnosticsMetadata());
    }

    private getSelectedProductsFromModel(): ProductosModel[] {
        const currentModel = this.getRuntimeModelSnapshot();
        const ids = String(currentModel.ids_productos ?? '')
            .split(';')
            .map((item) => item.trim())
            .filter((item) => item !== '');

        if (ids.length === 0) {
            return [];
        }

        return ids
            .map((id) => this.productById.get(id) ?? null)
            .filter((item): item is ProductosModel => item !== null);
    }

    private fetchProductDetail(product: ProductosModel): Promise<void> {
        const registro = String(product.num_registro ?? '').trim();

        if (registro === '') {
            return Promise.resolve();
        }

        if (this.productDetailCache.has(registro)) {
            return Promise.resolve();
        }

        const pendingRequest = this.productDetailRequests.get(registro);

        if (pendingRequest) {
            return pendingRequest;
        }

        const request = this.productCatalogCache
            .getOrLoad<CachedProductDetail>(
                this.getProductCacheConfig('magrama-detalle-producto', { registro }, PRODUCT_DETAIL_CACHE_TTL_MS),
                () => this.fetchProductDetailFromApi(registro, product)
            )
            .then((detail) => {
                this.productDetailCache.set(registro, detail);
            })
            .finally(() => {
                this.productDetailRequests.delete(registro);
            });

        this.productDetailRequests.set(registro, request);

        return request;
    }

    private fetchProductDetailFromApi(registro: string, product: ProductosModel): Promise<CachedProductDetail> {
        const scope = this.diagnostics?.start('fetch-product-detail', 'tratamientos-form', {
            ...this.getDiagnosticsMetadata(),
            registro,
            productId: product.id ?? null,
        });

        return this.formApi.magramaDetalleProducto
            .toPromise({
                referencia: `'${registro}'`
            } as never)
            .then((response: Uso[]) => {
                const detail = this.mapProductDetailResponse(response ?? []);

                scope?.addMetric('usos', detail.usos.length, 'items');
                scope?.addMetric('agentes', detail.agentes.length, 'items');
                scope?.end(this.getDiagnosticsMetadata());

                return detail;
            })
            .catch((error: unknown) => {
                scope?.fail(error, this.getDiagnosticsMetadata());

                return {
                    usos: [{ label: 'Uso...', value: null }],
                    agentes: []
                };
            });
    }

    private mapProductDetailResponse(response: Uso[]): CachedProductDetail {
        const seenUsoLabels = new Set<string>();
        const seenAgenteUsageLabels = new Set<string>();

        const usos = response
            .map((item) => ({
                label: item.uso ?? '',
                value: item
            }))
            .filter((item) => {
                const usageLabel = this.normalizeOptionLabel(item.label);

                if (usageLabel === '' || seenUsoLabels.has(usageLabel)) {
                    return false;
                }

                seenUsoLabels.add(usageLabel);
                return true;
            });

        const agentes = response
            .map((item) => {
                const normalized: Uso = {
                    ...item,
                    minDosis: item.dosis1 ?? 0,
                    maxDosis: item.dosis2 ?? 0,
                    dosis_min: item.dosis1 ?? 0,
                    dosis_max: item.dosis2 ?? 0,
                    isInvalid: false
                };

                return {
                    label: normalized.agente ?? '',
                    value: normalized
                };
            })
            .filter((item) => {
                const usageLabel = this.normalizeOptionLabel(item.value?.uso ?? '');
                const agentLabel = this.normalizeOptionLabel(item.label);
                const uniqueKey = `${usageLabel}::${agentLabel}`;

                if (agentLabel === '' || seenAgenteUsageLabels.has(uniqueKey)) {
                    return false;
                }

                seenAgenteUsageLabels.add(uniqueKey);
                return true;
            });

        return {
            usos: [{ label: 'Uso...', value: null }, ...usos],
            agentes
        };
    }

    private getProductCacheConfig(
        name: string,
        params: Record<string, unknown> = {},
        ttlMs: number = PRODUCT_CACHE_TTL_MS
    ): { namespace: string; name: string; ttlMs: number; params: Record<string, unknown>; userScope: string; clientScope: string } {
        return {
            namespace: 'tratamientos-form',
            name,
            ttlMs,
            params,
            userScope: String(StorageManager.getUser()?.id ?? 'anonymous'),
            clientScope: String(StorageManager.getClient()?.id ?? 'no-client')
        };
    }

    private getDiagnosticsMetadata(): Record<string, unknown> {
        return {
            formType: this.getType(),
            requestType: this.form?.formType ?? null,
            loadingData: this.loadingData,
            dropdownsFinalized: this.dropdownsFinalized,
            rawClients: this.rawClients.length,
            rawProducts: this.rawProducts.length,
            cachedProductDetails: this.productDetailCache.size,
        };
    }
}
